/**
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 *
 * @format
 */

import { Logger } from "@html-core";

export class HTML5FileSystemFileHandler {
   private name;
   private isDirectory: boolean = false;
   private isFile: boolean = true;
   private fullPath: string;
   private metaData;
   private parentHandler;
   private fileHandler;
   private fileEntry;
   private parentPath;
   public removeNodeInParent;
   public writer = {
      handler: null,
      addedInCache: false
   };

   constructor() {}

   public init = async (handler, directoryEntry) => {
      this.fileEntry = await handler.getFile();
      this.name = this.fileEntry.name;
      this.fullPath = "/" + this.name;
      this.parentHandler = directoryEntry.directoryHandler;
      this.fileHandler = await this.parentHandler.getFileHandle(this.name, { create: false });
      this.parentPath = directoryEntry.fullPath;
      this.metaData = {
         path: this.parentPath + "/" + this.fileEntry.name,
         name: this.fileEntry.name,
         size: this.fileEntry.size,
         type: this.fileEntry.type,
         webkitRelativePath: this.fileEntry.webkitRelativePath,
         lastModified: this.fileEntry.lastModified,
         modificationTime: this.fileEntry.lastModifiedDate
      };
   };

   public file = (FuncOnLoad, FuncOnError?) => {
      return new Promise((resolve, reject) => {
         if (FuncOnLoad) {
            FuncOnLoad(this.fileEntry);
            resolve("");
         }
      });
   };

   public createWriter = async (FuncOnLoad, FuncOnError?) => {
      const permission = await this.fileHandler.requestPermission({ mode: "readwrite" });
      if (permission === "granted") {
         if (!this.writer.handler) {
            this.writer.handler = await this.fileHandler.createWritable({ keepExistingData: true });
         }
         return new Promise(async (resolve, reject) => {
            if (FuncOnLoad) {
               FuncOnLoad(this.writer.handler);
               this.fileEntry = await this.fileHandler.getFile();
               if (this.fileEntry.size !== this.metaData.size) {
                  this.metaData.size = this.fileEntry.size;
               }
               resolve("");
            }
         });
      } else {
         Logger.info("CreateWriter: user denied permission.");
      }
   };

   public getMetadata = async (onLoad, FuncOnError) => {
      try {
         onLoad(this.metaData);
      } catch (e) {
         FuncOnError(e);
      }
   };

   private _remove = async () => {
      return new Promise((resolve, reject) => {
         try {
            this.parentHandler
               .removeEntry(this.name)
               .then(() => {
                  this.removeNodeInParent(this.name);
                  resolve(true);
               })
               .catch(async (e) => {
                  reject(e);
               });
         } catch (e) {
            Logger.info("remove failed");
            reject(e);
         }
      });
   };

   public remove = async (onSucceed, onError) => {
      const retryLimit = 8;

      let attempt = 0;
      for (let i = 0; i < retryLimit; i++) {
         attempt += 1;
         try {
            await this._remove();
            onSucceed();
            break;
         } catch (e) {
            Logger.info("Try removing " + attempt);
            Logger.info(e);
         }
         await this.delay(1000);
      }
      if (attempt >= retryLimit) {
         Logger.info("Failed to remove file " + this.name);
         onError();
      }
   };

   public removeRecursively = (onSucceed, onError) => {
      try {
         this.parentHandler.removeEntry(this.name, { recursive: true }).then(() => {
            onSucceed();
         });
      } catch (e) {
         onError(e);
      }
   };

   public moveTo = async (targetFolderEntry, targetFileName, onSuccess, onError) => {
      const isRename =
         this.parentHandler.isSameEntry(targetFolderEntry.directoryHandler) &&
         this.parentHandler.name === targetFolderEntry.name;
      if (isRename) {
         Logger.debug("rename a file");
         this.renameFile(targetFileName)
            .then(() => {
               this.fullPath = this.fullPath.replace(this.name, targetFileName);
               this.metaData.path = this.metaData.path.replace(this.name, targetFileName);
               this.name = targetFileName;
               this.metaData.name = targetFileName;
               onSuccess(targetFolderEntry);
            })
            .catch((e) => {
               onError(e);
            });
      } else {
         const targetHandler = targetFolderEntry.directoryHandler;
         //Todo
         this.moveFile(targetHandler, targetFileName)
            .then(() => {
               onSuccess(targetFolderEntry);
            })
            .catch((e) => {
               onError(e);
            });
         Logger.debug("move node to another folder");
      }
   };

   private _renameFile = (targetFileName) => {
      return new Promise((resolve, reject) => {
         this.fileHandler
            .move(targetFileName)
            .then(() => {
               resolve(true);
            })
            .catch((e) => {
               reject(e);
            });
      });
   };

   private renameFile = async (targetFileName) => {
      const retryLimit = 8;
      return new Promise(async (resolve, reject) => {
         let attempt = 0;
         for (let i = 0; i < retryLimit; i++) {
            attempt += 1;
            try {
               await this._renameFile(targetFileName);
               resolve(true);
               break;
            } catch (e) {
               Logger.info("Try renaming " + attempt);
               Logger.info(e);
            }
            await this.delay(1000);
         }
         if (attempt >= retryLimit) {
            reject("Failed to rename file " + this.name);
         }
      });
   };

   private delay = async (ms: number) => {
      await new Promise((resolve) => setTimeout(resolve, ms));
   };

   private moveFile = (target, name) => {
      return new Promise((resolve, reject) => {
         this.fileHandler
            .move(target, name)
            .then(() => {
               resolve(true);
            })
            .catch((e) => {
               reject(e);
            });
      });
   };
}
